import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import yup from "yup";
import { DevTool } from "@hookform/devtools";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  InputLabel,
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  makeStyles
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import {
  ConsultantQuality_GetConsultantNew,
  CriminalRecordType,
  TodoPriority,
  useRequestCriminalRecordCheckMutation
} from "@bonliva-crm/graphql";
import { Color, useSnackbarsPipeline } from "@bonliva-crm/ui";

type Props = {
  open: boolean;
  consultantId: string;
  qualityCheckName: string;
  qualityTemplate: string;
  types: CriminalRecordType[];
  onClose: () => void;
};

interface FormValues {
  criminalRecordType: string;
  priority: string;
  sendEmail: boolean;
}

export function RequestCriminalRecordCheckModal(props: Props) {
  const classes = useStyles();
  const snack = useSnackbarsPipeline();
  const [requestCriminalCheck] = useRequestCriminalRecordCheckMutation();

  const form = useForm<FormValues>({
    defaultValues: {
      criminalRecordType: "",
      priority: TodoPriority.NoStatus,
      sendEmail: true
    },
    resolver: yupResolver(
      yup.object().shape({
        criminalRecordType: yup.string().required(),
        priority: yup.string().required(),
        sendEmail: yup.boolean().required()
      })
    )
  });

  useEffect(() => {
    if (props.open) {
      const type = props.types.length === 1 ? props.types[0] : "";
      form.reset({
        criminalRecordType: type,
        priority: TodoPriority.NoStatus,
        sendEmail: true
      });
    }
  }, [form, props.open, props.types]);

  const onSubmit = async (values: FormValues) => {
    return new Promise((resolve, reject) => {
      requestCriminalCheck({
        variables: {
          consultantId: props.consultantId,
          qualityCheckName: props.qualityCheckName,
          qualityTemplate: props.qualityTemplate,
          params: values
        },
        refetchQueries: [ConsultantQuality_GetConsultantNew.OperationName],
        onCompleted: () => {
          snack.showSuccess("Beställningen har skickats");
          props.onClose();
          resolve(null);
        },
        onError: (error) => {
          snack.showError(error.message);
          reject();
        }
      });
    });
  };

  const canSubmit = !form.formState.isSubmitting && form.formState.isValid;

  return (
    <Dialog open={props.open} fullWidth maxWidth="sm" onClose={props.onClose}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogTitle>Beställ Belastningsregister</DialogTitle>

        <DialogContent>
          <Box>
            <Typography variant="body1" className={classes.description}>
              OBS!
              <br />
              <br />
              Vid beställning kommer ett mail med instruktioner gå ut direkt till
              konsulten men från kvks mailadress. Om du valt prioritet hög kommer
              ämnesraden innehålla ordet “PRIO” - vill du fortsätta?
            </Typography>
          </Box>

          <Box mt={2}>
            <Controller
              name="sendEmail"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                      color="primary"
                    />
                  }
                  label={locale.sendEmailToConsultant}
                />
              )}
            />
          </Box>

          <Box mt={2}>
            <Controller
              name="criminalRecordType"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormControl
                    variant="outlined"
                    fullWidth
                    required
                    disabled={props.types.length <= 1}
                    {...field}
                  >
                    <InputLabel id="criminal-record-type">
                      {locale.CriminalRecord}
                    </InputLabel>

                    <Select
                      labelId="criminal-record-type"
                      name="type"
                      value={field.value}
                      onChange={field.onChange}
                      autoFocus
                      classes={{
                        root: classes.selectRoot
                      }}
                    >
                      {props.types.map((type) => (
                        <MenuItem key={type} value={type}>
                          {locale.criminalRecordType[type]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }}
            ></Controller>
          </Box>

          <Box mt={2}>
            <Controller
              name="priority"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormControl fullWidth required {...field}>
                    <InputLabel id="priority">{locale.Priority}</InputLabel>

                    <Select
                      labelId="priority"
                      name="priority"
                      value={field.value}
                      onChange={field.onChange}
                      classes={{
                        root: classes.selectRoot
                      }}
                    >
                      <MenuItem value={TodoPriority.NoStatus}>Ingen prioritet</MenuItem>
                      <MenuItem value={TodoPriority.Low}>Låg prioritet</MenuItem>
                      <MenuItem value={TodoPriority.High}>Hög prioritet</MenuItem>
                    </Select>
                  </FormControl>
                );
              }}
            ></Controller>
          </Box>

          <DevTool control={form.control} />
        </DialogContent>

        <DialogActions>
          <Button onClick={props.onClose}>{locale.Cancel}</Button>

          <Button color="primary" variant="contained" type="submit" disabled={!canSubmit}>
            {locale.Order}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const useStyles = makeStyles((theme) => ({
  selectRoot: {
    border: "none !important"
  },
  description: {
    color: "white",
    background: Color.PrimaryDark,
    fontWeight: 500,
    padding: 8,
    borderRadius: 4
  }
}));
